/**
 * Audit Logger Module for Recipe Admin Metadata
 * Manages the `/recipes/{recipeId}/admin_metadata/meta` subdocument.
 * Tracks creator, last modifier, versioning, and chronological change history.
 */

export const ACTION_TYPES = {
  RECIPE_CREATED: "recipe_created",
  STATUS_TRANSITION: "status_transition",
  RECIPE_EDITED: "recipe_edited",
  PHOTO_REGENERATED: "photo_regenerated",
  PHOTO_UPLOADED: "photo_uploaded",
  COOKWARE_UPDATED: "cookware_updated",
  SOURCE_UPDATED: "source_updated"
};

/**
 * Extracts a normalized user info object from currentUser or auth object.
 * @param {Object} currentUser 
 * @returns {{ uid: string, email: string, display_name: string }}
 */
function extractUserInfo(currentUser) {
  if (!currentUser) {
    return {
      uid: "unknown_admin",
      email: "unknown",
      display_name: "Admin"
    };
  }
  return {
    uid: currentUser.uid || "unknown_admin",
    email: currentUser.email || "",
    display_name: currentUser.displayName || currentUser.display_name || (currentUser.email ? currentUser.email.split("@")[0] : "Admin")
  };
}

/**
 * Records an audit log event for a recipe.
 * Supports both named object arguments and positional parameters.
 *
 * @param {Object|any} optionsOrDb - Options object or Firestore db instance
 * @param {Function} [setDocParam] - Firestore setDoc function
 * @param {Function} [docParam] - Firestore doc function
 * @param {Function} [getDocParam] - Firestore getDoc function
 * @param {string} [recipeIdParam] - Target recipe ID
 * @param {string} [actionParam] - Action name (e.g. "recipe_created", "status_transition", etc.)
 * @param {string} [summaryParam] - Human-readable summary of the action
 * @param {Object} [detailsParam] - Optional structured metadata/diff details
 * @param {Object} [currentUserParam] - Currently authenticated user
 * @returns {Promise<Object|null>} The updated admin metadata payload
 */
export async function recordRecipeAuditLog(
  optionsOrDb,
  setDocParam,
  docParam,
  getDocParam,
  recipeIdParam,
  actionParam,
  summaryParam,
  detailsParam,
  currentUserParam,
  sourceParam
) {
  let db, setDoc, doc, getDoc, recipeId, action, summary, details, currentUser, source;

  if (
    typeof optionsOrDb === "object" &&
    optionsOrDb !== null &&
    (optionsOrDb.recipeId !== undefined || optionsOrDb.action !== undefined || (!setDocParam && optionsOrDb.db !== undefined))
  ) {
    ({
      db,
      setDoc,
      doc,
      getDoc,
      recipeId,
      action,
      summary,
      details,
      currentUser,
      source
    } = optionsOrDb);
  } else {
    db = optionsOrDb;
    setDoc = setDocParam;
    doc = docParam;
    getDoc = getDocParam;
    recipeId = recipeIdParam;
    action = actionParam;
    summary = summaryParam;
    details = detailsParam;
    currentUser = currentUserParam;
    source = sourceParam;
  }

  if (!recipeId) {
    console.error("❌ recordRecipeAuditLog: recipeId is required.");
    return null;
  }

  if (!db || !doc || !setDoc || !getDoc) {
    console.error("❌ recordRecipeAuditLog: Firestore functions (db, doc, setDoc, getDoc) are required.");
    return null;
  }

  const now = new Date().toISOString();
  const userObj = extractUserInfo(currentUser);

  try {
    const metaRef = doc(db, "recipes", recipeId, "admin_metadata", "meta");
    const metaSnap = await getDoc(metaRef);
    const existingData = metaSnap.exists() ? metaSnap.data() : {};

    const createdBy = existingData.created_by || userObj;
    const createdAt = existingData.created_at || now;
    const currentVersion = typeof existingData.version === "number" ? existingData.version : 0;
    const newVersion = currentVersion + 1;

    const event = {
      action: action || ACTION_TYPES.RECIPE_EDITED,
      summary: summary || "Recipe updated",
      details: details && typeof details === "object" ? details : {},
      timestamp: now,
      performed_by: userObj,
      version: newVersion
    };

    const existingLog = Array.isArray(existingData.change_log) ? existingData.change_log : [];
    // Prepend newest event first, cap at latest 100 entries
    const changeLog = [event, ...existingLog].slice(0, 100);

    const payload = {
      created_by: createdBy,
      created_at: createdAt,
      last_modified_by: userObj,
      last_modified_at: now,
      version: newVersion,
      change_log: changeLog
    };

    if (source !== undefined) {
      payload.source = source;
    } else if (existingData.source) {
      payload.source = existingData.source;
    }

    await setDoc(metaRef, payload, { merge: true });
    console.log(`🛡️ [AuditLog] Recorded '${event.action}' for recipe '${recipeId}' (v${newVersion}) by ${userObj.email || userObj.uid}`);
    return payload;
  } catch (err) {
    console.error(`❌ recordRecipeAuditLog failed for recipe '${recipeId}':`, err);
    throw err;
  }
}

/**
 * Updates the source origin metadata in /recipes/{recipeId}/admin_metadata/meta.
 */
export async function updateRecipeSourceMetadata({
  db,
  setDoc,
  doc,
  getDoc,
  recipeId,
  source,
  currentUser
}) {
  const url = source?.url || null;
  const summary = url ? `Updated source link to ${url}` : "Cleared source link";
  return await recordRecipeAuditLog({
    db,
    setDoc,
    doc,
    getDoc,
    recipeId,
    action: ACTION_TYPES.SOURCE_UPDATED,
    summary,
    details: { source },
    source,
    currentUser
  });
}

/**
 * Fetches admin metadata subdocument for a recipe.
 * Supports both named object arguments and positional parameters.
 *
 * @param {Object|any} optionsOrDb - Options object or Firestore db instance
 * @param {Function} [docParam] - Firestore doc function
 * @param {Function} [getDocParam] - Firestore getDoc function
 * @param {string} [recipeIdParam] - Target recipe ID
 * @returns {Promise<Object|null>} The recipe admin metadata, or null if not found
 */
export async function fetchRecipeAdminMetadata(optionsOrDb, docParam, getDocParam, recipeIdParam) {
  let db, doc, getDoc, recipeId;

  if (
    typeof optionsOrDb === "object" &&
    optionsOrDb !== null &&
    (optionsOrDb.recipeId !== undefined || (!docParam && optionsOrDb.db !== undefined))
  ) {
    ({ db, doc, getDoc, recipeId } = optionsOrDb);
  } else {
    db = optionsOrDb;
    doc = docParam;
    getDoc = getDocParam;
    recipeId = recipeIdParam;
  }

  if (!recipeId) {
    console.warn("⚠️ fetchRecipeAdminMetadata: recipeId is required.");
    return null;
  }

  if (!db || !doc || !getDoc) {
    console.warn("⚠️ fetchRecipeAdminMetadata: Firestore functions (db, doc, getDoc) are required.");
    return null;
  }

  try {
    const metaRef = doc(db, "recipes", recipeId, "admin_metadata", "meta");
    const metaSnap = await getDoc(metaRef);
    if (metaSnap.exists()) {
      return metaSnap.data();
    }
    return null;
  } catch (err) {
    console.error(`❌ fetchRecipeAdminMetadata failed for recipe '${recipeId}':`, err);
    return null;
  }
}
